import { useEffect, useState } from 'react';
import { useTranslation } from 'react-i18next';
import { toast } from 'react-toastify';

import { Logger } from '@app/core/logger/logger';

function readFromStorage<T>(storageKey: string): Partial<T> | null {
  try {
    const raw = localStorage.getItem(storageKey);
    if (!raw) return null;
    return JSON.parse(raw) as Partial<T>;
  } catch {
    return null;
  }
}

function mergeWithDefaults<T extends Record<string, any>>(
  defaults: T,
  stored: Partial<T>,
): T {
  const result = { ...defaults };
  for (const key of Object.keys(defaults) as (keyof T)[]) {
    const defaultVal = defaults[key];
    const storedVal = stored[key];
    // eslint-disable-next-line no-continue
    if (storedVal === undefined) continue;
    if (Array.isArray(defaultVal) && Array.isArray(storedVal)) {
      // Preserve stored order, drop items no longer in the defaults (e.g.
      // removed columns), append items added since the options were saved
      const defaultSet = new Set(defaultVal);
      const preservedItems = (storedVal as unknown[]).filter((item) =>
        defaultSet.has(item),
      );
      const preservedSet = new Set(preservedItems);
      const newItems = (defaultVal as unknown[]).filter(
        (item) => !preservedSet.has(item),
      );
      result[key] = [...preservedItems, ...newItems] as T[keyof T];
    } else if (
      typeof defaultVal === 'object' &&
      defaultVal !== null &&
      !Array.isArray(defaultVal) &&
      typeof storedVal === 'object' &&
      storedVal !== null
    ) {
      // Shallow-merge objects: keep only keys still present in the defaults,
      // so removed columns don't linger, and new keys get their default value
      const prunedStoredVal = Object.fromEntries(
        Object.entries(storedVal).filter(
          ([storedKey]) => storedKey in defaultVal,
        ),
      );
      result[key] = { ...defaultVal, ...prunedStoredVal } as T[keyof T];
    } else {
      result[key] = storedVal as T[keyof T];
    }
  }
  return result;
}

export function useLocalStorageOptions<T extends Record<string, any>>(
  defaultOptions: T,
  storageKey: string,
): [T, React.Dispatch<React.SetStateAction<T>>] {
  const { t } = useTranslation();
  const [options, setOptions] = useState<T>(() => {
    const stored = readFromStorage<T>(storageKey);
    if (!stored) return defaultOptions;
    return mergeWithDefaults(defaultOptions, stored);
  });

  useEffect(() => {
    try {
      localStorage.setItem(storageKey, JSON.stringify(options));
    } catch (error) {
      Logger.error(error);
      toast.error(
        t('global.table.columns.error', {
          error: error instanceof Error ? error.message : t('global.unknown'),
        }),
      );
    }
  }, [options, storageKey, t]);

  return [options, setOptions];
}
